import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ShieldCheck, ArrowRight, Facebook, Linkedin, Instagram, Globe } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { BRAND, NAV, SOCIAL } from "@/data/content";
import { withLocale } from "@/lib/seo";
import logo from "@/assets/smart-logo.webp";

export const Footer = () => {
  const { t, isAr, lang } = useLang();
  const year = new Date().getFullYear();
  const Arrow = () => <ArrowRight className={`h-4 w-4 ${isAr ? "rotate-180" : ""}`} />;

  return (
    <footer className="bg-navy-deep text-white" data-testid="site-footer">
      <div className="container-tnsq pt-20 pb-10">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-end pb-14 border-b border-white/10">
          <div>
            <p className="kicker text-gold mb-4">{t({ en: "Get in touch", ar: "لنتحدث" })}</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.08] text-white">
              {t({ en: "Let's put your numbers", ar: "لنضع أرقامك" })}
              <br />
              <span className="text-gold">{t({ en: "in perfect order.", ar: "في نصابها الصحيح." })}</span>
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
            <a
              href={`https://wa.me/${BRAND.phoneRaw}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-brandgreen hover:bg-brandgreen-600 font-semibold transition-colors"
            >
              {t({ en: "WhatsApp Smart Account Vision", ar: "راسلنا على واتساب" })}
            </a>
            <Link
              to={withLocale(lang, "/#contact")}
              data-testid="footer-contact-cta"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-white text-navy-deep hover:bg-gold font-semibold transition-colors"
            >
              {t({ en: "Send a message", ar: "أرسل رسالة" })} <Arrow />
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1.3fr] gap-10 pt-14">
          <div>
            <img src={logo} alt={t(BRAND.name)} className="h-14 w-auto bg-white rounded-lg p-2 mb-5" />
            <p className="text-white/55 text-sm leading-relaxed max-w-sm">
              {t({
                en: "Tax advisory, accounting and compliance support for businesses across the UAE — built on accurate records and clear next steps.",
                ar: "استشارات ضريبية ومحاسبة ودعم امتثال للأعمال في مختلف إمارات الدولة، بالاعتماد على سجلات دقيقة وخطوات واضحة.",
              })}
            </p>
            <div className="mt-6 inline-flex items-center gap-3 rounded-xl border border-gold/30 bg-white/[0.04] px-4 py-3">
              <ShieldCheck className="h-6 w-6 text-gold" />
              <div className="leading-tight">
                <div className="text-sm font-semibold">{t(BRAND.agency)}</div>
                <div className="text-xs text-white/50" dir="ltr">TAAN No. {BRAND.ftaNo}</div>
              </div>
            </div>
            <div className="mt-6">
              <h4 className="text-xs font-semibold tracking-wider uppercase text-gold/80 mb-3">{t({ en: "Follow Smart Account Vision", ar: "تابع سمارت أكاونت فيجن" })}</h4>
              <div className="flex flex-col gap-2 text-[0.9rem]">
                <a href={SOCIAL.website} target="_blank" rel="noopener" data-testid="social-website" className="inline-flex items-center gap-2 text-white/70 hover:text-gold transition-colors" dir="ltr"><Globe className="h-4 w-4" /> {SOCIAL.websiteLabel}</a>
                <a href={SOCIAL.facebook} target="_blank" rel="noopener" data-testid="social-facebook" className="inline-flex items-center gap-2 text-white/70 hover:text-gold transition-colors"><Facebook className="h-4 w-4" /> Smart Account Vision on Facebook</a>
                <a href={SOCIAL.linkedin} target="_blank" rel="noopener" data-testid="social-linkedin" className="inline-flex items-center gap-2 text-white/70 hover:text-gold transition-colors"><Linkedin className="h-4 w-4" /> Smart Account Vision on LinkedIn</a>
                {SOCIAL.instagram && <a href={SOCIAL.instagram} target="_blank" rel="noopener" data-testid="social-instagram" className="inline-flex items-center gap-2 text-white/70 hover:text-gold transition-colors"><Instagram className="h-4 w-4" /> Smart Account Vision on Instagram</a>}
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase text-gold/80 mb-5">{t({ en: "Explore", ar: "تصفّح" })}</h4>
            <ul className="space-y-3">
              {NAV.map((item) => (
                <li key={item.key}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="noopener noreferrer" data-testid={`footer-nav-${item.key}`} className="text-white/70 hover:text-gold transition-colors text-[0.95rem]">
                      {t(item.label)}
                    </a>
                  ) : (
                    <Link to={item.external ? item.to : withLocale(lang, item.to)} data-testid={`footer-nav-${item.key}`} className="text-white/70 hover:text-gold transition-colors text-[0.95rem]">
                      {t(item.label)}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase text-gold/80 mb-5">{t({ en: "Contact", ar: "التواصل" })}</h4>
            <ul className="space-y-4 text-[0.95rem]">
              <li className="flex gap-3"><MapPin className="h-5 w-5 text-gold shrink-0 mt-0.5" /><span className="text-white/70">{t(BRAND.address)}</span></li>
              <li className="flex gap-3"><Phone className="h-5 w-5 text-gold shrink-0 mt-0.5" /><div className="flex flex-col"><a href={`tel:${BRAND.phoneRaw}`} className="text-white/70 hover:text-gold transition-colors" dir="ltr">{BRAND.phone}</a><span className="text-white/40 text-xs">{t(BRAND.phoneRole)}</span><a href={`tel:${BRAND.phone2Raw}`} className="text-white/70 hover:text-gold transition-colors mt-1.5" dir="ltr">{BRAND.phone2}</a><span className="text-white/40 text-xs">{t(BRAND.phone2Role)}</span></div></li>
              <li className="flex gap-3"><Mail className="h-5 w-5 text-gold shrink-0 mt-0.5" /><div className="flex flex-col"><a href={`mailto:${BRAND.email}`} className="text-white/70 hover:text-gold transition-colors" dir="ltr">{BRAND.email}</a><a href={`mailto:${BRAND.email2}`} className="text-white/70 hover:text-gold transition-colors" dir="ltr">{BRAND.email2}</a></div></li>
              <li className="flex gap-3"><Clock className="h-5 w-5 text-gold shrink-0 mt-0.5" /><span className="text-white/70">{t(BRAND.hours)}</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row gap-3 md:items-center md:justify-between text-xs text-white/40">
          <span>© {year} {t(BRAND.name)}. {t({ en: "All rights reserved.", ar: "جميع الحقوق محفوظة." })} <Link to="/admin" data-testid="footer-admin-link" className="ms-2 text-white/30 hover:text-gold transition-colors">{t({ en: "Admin", ar: "الإدارة" })}</Link></span>
          <span className="max-w-xl">
            {t({
              en: "Tax information on this website is general and may change. Verify current rules with official FTA/MoF sources before acting.",
              ar: "المعلومات الضريبية على هذا الموقع عامة وقد تتغير. يُرجى التحقق من القواعد السارية عبر مصادر الهيئة ووزارة المالية الرسمية قبل اتخاذ أي إجراء.",
            })}
          </span>
        </div>
      </div>
    </footer>
  );
};
